import * as React from 'react'

import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { endOfMonth, startOfMonth } from 'date-fns'
import moment from 'moment'

import { observer } from 'mobx-react'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import FiltersForm from 'modules/core/lib/forms/Filters'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import hasRegularAccess from 'modules/employees/lib/hasRegularAccess'
import locations from 'modules/locations/store/collections/locations'
import teams from 'modules/teams/store/collections/teams'
import leaveTypes from 'modules/timeoff/store/collections/leaveTypes'

export interface RenderProps {
  filtersForm: FiltersForm
  onDateRangeChange: (newRange: [startDate: Date, endDate: Date]) => void
  date: moment.Moment
  loading: boolean
  range: [Date, Date]
  canSeeCalendar: boolean
}

const CalendarRoot = ({ children }: React.ConsumerProps<RenderProps>) => {
  const { canManageTimeoff, employee } = useCurrentSession()

  const [localFilterByValue] = useLocalStorage<string>(LOCAL_STORAGE_KEYS.calendarFilter)

  const isLocationValid = () => {
    if (!localFilterByValue?.includes('location')) return false

    const match = localFilterByValue.match(/\d+/)
    const locationId = match && typeof match[0] === 'string' ? parseInt(match[0]) : null

    return !!locations.find({ id: locationId })
  }

  const isTeamValid = (): boolean => {
    if (!localFilterByValue?.includes('team')) return false

    const match = localFilterByValue.match(/\d+/)
    const teamId = match && typeof match[0] === 'string' ? parseInt(match[0]) : null

    return !!teams.find({ id: teamId })
  }

  const byLocalFilter = () => {
    if (!localFilterByValue?.includes('location') && !localFilterByValue?.includes('team')) {
      return localFilterByValue
    } else if (isTeamValid() || isLocationValid()) {
      return localFilterByValue
    }

    return ''
  }

  const filtersForm = React.useMemo(
    () =>
      new FiltersForm({
        sortBy: 'first_name-asc',
        filterBy:
          byLocalFilter() ||
          (employee && !canManageTimeoff
            ? `location___${employee.locationId}`
            : 'activity___active'),
      }),
    [localFilterByValue]
  )

  const [range, setRange] = React.useState<[Date, Date]>([
    startOfMonth(new Date()),
    endOfMonth(new Date()),
  ])

  const onDateRangeChange = (newRange: [startDate: Date, endDate: Date]) => {
    setRange(newRange)
    onChangeDate(moment(newRange[0]))
  }

  const [date, setDate] = React.useState(moment())

  const onChangeDate = (changedDate: moment.Moment) => {
    setDate(changedDate)
  }

  const { loading } = useAsyncData(() => leaveTypes.fetch(), [])

  return children({
    loading,
    date,
    filtersForm,
    range,
    onDateRangeChange,
    canSeeCalendar: !employee || hasRegularAccess(employee.hasRegularAccess),
  })
}

export default observer(CalendarRoot)
